import { Loader2, Sparkles, StickyNote } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { answerQuestion, createQuestionCard, type QuestionAnswer } from '@/api/services/book-ai'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { memoPath } from '@/routes/paths'

import { CardCreatedNotice } from './card-created-notice'
import { CitationChip } from './citation-chip'
import { SimilarCardsNotice } from './similar-cards-notice'
import { SimpleMarkdown } from './simple-markdown'
import { useSimilarCardsGate } from './useSimilarCardsGate'

type Props = {
  episodeId: number
  sectionIndex: number
  question: string
  onNavigate: (key: number) => void
}

const Option = ({
  checked,
  onChange,
  children,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  children: React.ReactNode
}) => (
  <label className="flex items-center gap-2 text-xs">
    <Checkbox checked={checked} onCheckedChange={(value) => onChange(value === true)} />
    {children}
  </label>
)

/**
 * 「読む前の問い」に答えてカードにする
 *
 * 先に自分の言葉で答え（思い出す練習）、そのあと章の本文から作った AI の答えと講評を見比べてからカードにする。
 * カードには出典（本と章）・自分の答え・選んだ補足と根拠・その章のハイライトが入る。
 * 他のカードとのつながりは、書き終わってからメモ画面の「関連の提案」で付ける。
 * ただし、ほぼ同じことを書いたカードがすでにあれば作る前に知らせ、つなぐかを選べるようにする。
 */
export const QuestionCardPanel = ({ episodeId, sectionIndex, question, onNavigate }: Props) => {
  const navigate = useNavigate()
  const [draft, setDraft] = useState('')
  const [result, setResult] = useState<QuestionAnswer | null>(null)
  const [isAnswering, setIsAnswering] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [createdMemoId, setCreatedMemoId] = useState<number | null>(null)
  const [includeAiAnswer, setIncludeAiAnswer] = useState(false)
  const [includeQuotes, setIncludeQuotes] = useState(true)
  const [includeHighlights, setIncludeHighlights] = useState(true)
  const similarGate = useSimilarCardsGate()

  const compare = async () => {
    setIsAnswering(true)
    try {
      setResult(await answerQuestion(episodeId, { section_index: sectionIndex, question, user_answer: draft }))
    } catch (error) {
      console.error('問いの答えを取得できませんでした:', error)
    } finally {
      setIsAnswering(false)
    }
  }

  const create = async () => {
    if (!(await similarGate.check(`${question}\n${draft}`))) return
    setIsCreating(true)
    try {
      const card = await createQuestionCard(episodeId, {
        section_index: sectionIndex,
        question,
        user_answer: draft,
        ai_answer: result && includeAiAnswer ? result.answer : null,
        citation_chunk_ids: result && includeQuotes ? result.citations.map((c) => c.chunk_id) : [],
        include_highlights: includeHighlights,
        link_memo_ids: similarGate.linkIds,
      })
      setCreatedMemoId(card.memo_id)
      toast.success('カードを作りました', {
        action: { label: '開く', onClick: () => navigate(memoPath(card.memo_id)) },
      })
    } catch (error) {
      console.error('カードを作れませんでした:', error)
    } finally {
      setIsCreating(false)
    }
  }

  if (createdMemoId != null) return <CardCreatedNotice memoId={createdMemoId} />

  const canCreate = (!!draft.trim() || (!!result && includeAiAnswer)) && !isCreating && !similarGate.isChecking

  return (
    <div className="space-y-2.5 rounded-lg border p-2.5">
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="まず自分の言葉で答えてみる"
        rows={3}
        maxLength={2000}
        className="resize-none text-sm"
      />

      {!result && (
        <Button
          size="sm"
          variant="outline"
          className="w-full"
          disabled={!draft.trim() || isAnswering}
          onClick={compare}
        >
          {isAnswering ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
          AI の答えと比べる
        </Button>
      )}

      {result && (
        <div className="space-y-2">
          {result.feedback && (
            <div className="rounded-md bg-amber-500/10 p-2.5">
              <p className="mb-1 text-xs font-semibold text-amber-700 dark:text-amber-300">あなたの答えへのヒント</p>
              <p className="text-sm leading-relaxed wrap-break-word">{result.feedback}</p>
            </div>
          )}
          <div className="space-y-1.5 rounded-md bg-muted/60 p-2.5">
            <p className="text-xs font-semibold text-muted-foreground">本文から見た答え</p>
            <SimpleMarkdown text={result.answer} />
            {result.citations.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {result.citations.map((citation) => (
                  <CitationChip
                    key={citation.chunk_id}
                    citation={citation}
                    label={citation.excerpt}
                    onClick={() => onNavigate(citation.key)}
                  />
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">見比べて、上の自分の答えを書き直してからカードにできます</p>
          <Option checked={includeAiAnswer} onChange={setIncludeAiAnswer}>
            本文から見た答えを補足として入れる
          </Option>
          {result.citations.length > 0 && (
            <Option checked={includeQuotes} onChange={setIncludeQuotes}>
              根拠の本文を引用する（{result.citations.length}）
            </Option>
          )}
        </div>
      )}

      {(!result || result.highlight_count > 0) && (
        <Option checked={includeHighlights} onChange={setIncludeHighlights}>
          この章の自分のハイライトを引用する{result ? `（${result.highlight_count}）` : ''}
        </Option>
      )}

      <SimilarCardsNotice gate={similarGate} />

      <Button size="sm" className="w-full" disabled={!canCreate} onClick={create}>
        {isCreating || similarGate.isChecking ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <StickyNote className="size-4" />
        )}
        {similarGate.similar?.length ? 'このままカードにする' : 'カードにする'}
      </Button>
    </div>
  )
}
